import { DataTypeLocator, QueryNode } from '@helo/cms'

import { ProbeConcurrency } from './probeConcurrency.parts'
import {
  PROBE_INITIAL_ROW_COUNT,
  PROBE_PAGE_SIZE,
  PROBE_PARENT_CHECK_CONCURRENCY,
  ProbeRunResult,
  TableProbeRunner,
} from './tableProbeRunner.parts'

export type BatchedDetailSubTableTarget = {
  queryId: string
  connectionLocator: DataTypeLocator
  sampleColumnSelector?: DataTypeLocator
  detailViewPath: string
  fetchSubTablePage: (parentEntityId: string) => Promise<QueryNode | undefined>
}

export type BatchedDetailSubTableProbeResult = Record<string, ProbeRunResult>

export const DetailSubTableBatchProbe = {
  run: async (options: {
    targets: BatchedDetailSubTableTarget[]
    fetchParentPage: (
      skip: number,
      first: number,
    ) => Promise<QueryNode | undefined>
    parentConnectionLocator: DataTypeLocator
    parentEntityIdDataKey?: string
    rawEntityIdPath?: string[]
    preferredParentEntityId?: string
    onProgress?: (options: {
      pendingQueryIds: string[]
      parentsChecked: number
      parentTotalCount: number
    }) => void
    onPass?: (queryId: string, result: ProbeRunResult) => void
  }): Promise<BatchedDetailSubTableProbeResult> => {
    const {
      targets,
      fetchParentPage,
      parentConnectionLocator,
      parentEntityIdDataKey,
      rawEntityIdPath = [],
      preferredParentEntityId,
      onProgress,
      onPass,
    } = options

    const results: BatchedDetailSubTableProbeResult = {}
    let pending = [...targets]
    let parentTotalCount = 0
    let parentsChecked = 0

    const markPass = (
      target: BatchedDetailSubTableTarget,
      result: ProbeRunResult,
    ) => {
      results[target.queryId] = result
      pending = pending.filter(item => item.queryId !== target.queryId)
      onPass?.(target.queryId, result)
    }

    const probePendingForParent = async (parentEntityId: string) => {
      if (pending.length === 0) {
        return
      }

      parentsChecked += 1
      onProgress?.({
        pendingQueryIds: pending.map(item => item.queryId),
        parentsChecked,
        parentTotalCount,
      })

      const probeResults = await Promise.all(
        pending.map(async target => {
          const subTableQueryNode =
            await target.fetchSubTablePage(parentEntityId)
          const passResult = TableProbeRunner.hasTableRows(
            subTableQueryNode,
            target.connectionLocator,
            target.sampleColumnSelector,
          )

          if (!passResult) {
            return undefined
          }

          return {
            target,
            result: {
              status: 'pass' as const,
              totalCount: passResult.totalCount,
              itemCount: passResult.itemCount,
              sampleValue: passResult.sampleValue,
              recordsChecked: parentsChecked,
              passEntityId: parentEntityId,
              passRecordPath: target.detailViewPath.replace(
                ':id',
                parentEntityId,
              ),
            },
          }
        }),
      )

      probeResults.forEach(entry => {
        if (entry) {
          markPass(entry.target, entry.result)
        }
      })
    }

    if (preferredParentEntityId) {
      await probePendingForParent(preferredParentEntityId)
    }

    let parentSkip = 0
    let isFirstParentFetch = true

    while (pending.length > 0) {
      const pageSize = isFirstParentFetch
        ? PROBE_INITIAL_ROW_COUNT
        : PROBE_PAGE_SIZE
      const parentQueryNode = await fetchParentPage(parentSkip, pageSize)
      const parentPage = TableProbeRunner.extractListPageItems(
        parentQueryNode,
        parentConnectionLocator,
      )

      parentTotalCount = Math.max(parentTotalCount, parentPage.totalCount)

      let pageEntityIds = parentEntityIdDataKey
        ? parentPage.items
            .map(row =>
              TableProbeRunner.extractEntityIdFromRow(
                row,
                parentEntityIdDataKey,
              ),
            )
            .filter((entityId): entityId is string => Boolean(entityId))
        : []

      if (
        pageEntityIds.length === 0 &&
        parentSkip === 0 &&
        rawEntityIdPath.length > 0 &&
        parentQueryNode
      ) {
        const fallbackEntityId = getValueAtPath(
          parentQueryNode.apolloState?.data,
          rawEntityIdPath,
        )

        if (
          typeof fallbackEntityId === 'string' &&
          fallbackEntityId.length > 0
        ) {
          pageEntityIds = [fallbackEntityId]
        }
      }

      const idsToProbe = pageEntityIds.filter(
        parentEntityId => parentEntityId !== preferredParentEntityId,
      )

      await ProbeConcurrency.runWithConcurrency(
        idsToProbe,
        PROBE_PARENT_CHECK_CONCURRENCY,
        async parentEntityId => {
          await probePendingForParent(parentEntityId)
        },
      )

      if (pending.length === 0) {
        break
      }

      if (parentTotalCount === 0 || parentSkip + pageSize >= parentTotalCount) {
        break
      }

      parentSkip += isFirstParentFetch
        ? PROBE_INITIAL_ROW_COUNT
        : PROBE_PAGE_SIZE
      isFirstParentFetch = false
    }

    pending.forEach(target => {
      results[target.queryId] = {
        status: 'fail',
        totalCount: parentTotalCount,
        recordsChecked: parentsChecked,
        errorMessage:
          parentTotalCount === 0
            ? 'No parent records found in parent list for this environment'
            : `Checked ${parentsChecked} parent record${
                parentsChecked === 1 ? '' : 's'
              } — none returned sub-table rows`,
      }
    })

    return results
  },
}

const getValueAtPath = (value: unknown, path: string[]): unknown =>
  path.reduce<unknown>((current, segment) => {
    if (current === null || current === undefined) {
      return undefined
    }

    if (Array.isArray(current) && /^\d+$/.test(segment)) {
      return current[Number(segment)]
    }

    if (typeof current === 'object') {
      return (current as Record<string, unknown>)[segment]
    }

    return undefined
  }, value)
